import React, { memo, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  Icon,
  ScrollArea,
  Separator,
  Text,
  type IconName,
} from "@salestrak/ui";

import type { MapPin } from "./map-types";
import type { MapIssue } from "./map-issue";
import { InfoboxIssueCard } from "./infobox-issue-card";
import {
  useMapInfoboxData,
  type AddressDetails,
} from "#app/lib/hooks/use-map-infobox-data";
import { useIssueView } from "#app/components/issue-view";
import type { Omittable } from "@salestrak/ui";

/** Width of the map infobox popup (used for centering in map-view.tsx). */
export const INFOBOX_WIDTH = 340;

// Hoisted to avoid reconstructing on every render
const sqftFormatter = new Intl.NumberFormat();
const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

type MapInfoboxContentProps = Omittable<{
  pin: MapPin;
  onClose?: () => void;
  onAddProject?: () => void;
  showCloseButton?: boolean;
}>;

// ---------------------------------------------------------------------------
// Property details card (when no issues at pin)
// ---------------------------------------------------------------------------

type Row = { icon: IconName; label: string; value: string };

function PropertyDetailsCard({
  pin,
  addressDetails,
}: {
  pin: MapPin;
  addressDetails: AddressDetails | null;
}) {
  const displayAddress =
    addressDetails?.addressLine1 ?? pin.addressLine1 ?? pin.wholeAddress;
  const cityStateZip =
    [
      addressDetails?.city ?? pin.city,
      addressDetails?.stateProvince ?? pin.stateProvince,
      addressDetails?.postalCode ?? pin.postalCode,
    ]
      .filter(Boolean)
      .join(", ")
      .replace(/, (\d)/, " $1") || null;

  const details = pin.propertyDetails;

  const formattedSqft = details?.squareFootage
    ? `${sqftFormatter.format(details.squareFootage)} sqft`
    : null;

  const formattedMarketValue = details?.marketValue
    ? currencyFormatter.format(details.marketValue)
    : null;

  const rows: Row[] = [
    formattedSqft        ? { icon: "maximize-2", label: "Square Feet",     value: formattedSqft }             : null,
    details?.yearBuilt   ? { icon: "home",        label: "Year Built",      value: String(details.yearBuilt) } : null,
    details?.ownerNames?.[0] ? { icon: "user",    label: "Owner",           value: details.ownerNames[0] }     : null,
    formattedMarketValue ? { icon: "building",    label: "Estimated Value", value: formattedMarketValue }      : null,
  ].filter((r): r is Row => r !== null);

  return (
    // The rows run edge to edge under their separators, so the card carries no
    // padding of its own — each block pads itself.
    <Card radius="lg" className="overflow-hidden shadow-none">
      {(displayAddress || cityStateZip) && (
        <Box gap="xs" paddingBottom="md" paddingTop="md" paddingX="md">
          {displayAddress && (
            <Text size="sm" weight="semibold">
              {displayAddress}
            </Text>
          )}
          {cityStateZip && (
            <Text size="xs" variant="muted" weight="medium">
              {cityStateZip}
            </Text>
          )}
        </Box>
      )}

      {rows.length > 0 && (
        <>
          <Separator />
          {rows.map((row, i) => (
            <Box key={row.label} align="center" direction="row" gap="sm" paddingLeft="md">
              <Icon color="muted" name={row.icon} size="md" />
              <Box fill="flex">
                <Box align="center" direction="row" justify="between" paddingRight="md" paddingY="sm">
                  <Text size="sm" variant="muted">{row.label}</Text>
                  <Text size="sm">{row.value}</Text>
                </Box>
                {i < rows.length - 1 && <Separator />}
              </Box>
            </Box>
          ))}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface MapInfoboxContentViewProps extends MapInfoboxContentProps {
  issues: MapIssue[];
  addressDetails: AddressDetails | null;
  loading: boolean;
  /** Called when an issue card is clicked. */
  onIssueClick: (issue: MapIssue) => void;
  /** Called when the activity badge is clicked. */
  onActivityClick?: (issue: MapIssue) => void;
  /** Show assignee selector on issue cards (requires Apollo context). */
  showAssigneeSelector?: boolean;
}

/**
 * Pure presentational infobox — receives pre-fetched data as props.
 * Renders at natural height; the parent decides how to bound/scroll it
 * (the mobile drawer scrolls the whole thing; the InfoWindow wrapper caps
 * the visible height).
 */
export const MapInfoboxContentView = memo(function MapInfoboxContentView({
  pin,
  issues,
  addressDetails,
  onClose,
  onAddProject,
  onIssueClick,
  onActivityClick,
  showAssigneeSelector = true,
}: MapInfoboxContentViewProps) {
  const hasIssues = pin.issueIds.length > 0;
  const displayAddress = pin.addressLine1 || pin.wholeAddress;

  return (
    <Box gap="sm" padding="sm">
      {/* Header */}
      <Box align="center" direction="row" justify="between">
        <Box align="center" direction="row" fill="flex" gap="sm">
          {hasIssues ? (
            <>
              <Icon name="map-pin" size="lg" />
              <Text size="sm" truncate={1} weight="semibold">
                {displayAddress ?? "Location"}
              </Text>
            </>
          ) : (
            <>
              <Icon name="plus" size="lg" />
              <Text size="sm" weight="semibold">Add project</Text>
            </>
          )}
        </Box>
        {onClose && (
          <Button
            aria-label="Close info window"
            onClick={(event) => {
              event.stopPropagation();
              onClose();
            }}
            size="icon-sm"
            variant="ghost"
          >
            <Icon name="x" size="lg" />
          </Button>
        )}
      </Box>

      {/* Content — gate on data presence, not loading, to avoid layout shift / flicker */}
      {hasIssues ? (
        <Box gap="xs">
          {issues.map((issue) => (
            <InfoboxIssueCard
              key={issue.issueId}
              issue={issue}
              onActivityClick={onActivityClick}
              onClick={onIssueClick}
              showAssigneeSelector={showAssigneeSelector}
            />
          ))}
        </Box>
      ) : (
        <PropertyDetailsCard addressDetails={addressDetails} pin={pin} />
      )}

      {/* Add project button */}
      {onAddProject && (
        // A flex column, so the inline-flex Button stretches edge to edge; a
        // bare Box is display:block and would leave it at content width.
        <Box direction="column" paddingBottom="sm">
          {/* The primary variant paints its own foreground, so the icon no
            * longer needs an explicit white tint. */}
          <Button onClick={onAddProject}>
            <Icon name="plus" size="lg" />
            <Text weight="semibold">Add project</Text>
          </Button>
        </Box>
      )}
    </Box>
  );
});

/**
 * Self-contained infobox that fetches its own data via Apollo.
 * Use only when rendered inside the ApolloProvider tree (e.g. the map InfoWindow).
 */
export function MapInfoboxContent(props: MapInfoboxContentProps) {
  const { issues, addressDetails, loading } = useMapInfoboxData(props.pin);
  const { openIssue } = useIssueView();

  // No imperative re-center — the map screen owns a single effect that pans
  // whenever (selectedPin, drawerWidth) changes. Opening the drawer flips
  // drawerWidth, which re-fires that effect.
  const handleIssueClick = useCallback(
    (issue: MapIssue) => openIssue(issue),
    [openIssue],
  );

  const handleActivityClick = useCallback(
    (issue: MapIssue) => openIssue(issue, { initialTab: "activity" }),
    [openIssue],
  );

  // Stop wheel events from propagating to the Google Map (which captures
  // them for zoom), so the scroller inside the infobox can scroll.
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const stop = (e: WheelEvent) => e.stopPropagation();
    el.addEventListener("wheel", stop, { passive: true });
    return () => el.removeEventListener("wheel", stop);
  }, []);

  return (
    <Box
      direction="column"
      touchAction="manipulation"
      background="popover"
      maxHeight="60dvh"
      ref={containerRef}
      rounded="md"
      width={INFOBOX_WIDTH}
    >
      {/* The scrollbar is visible chrome here (the popup is capped), so this is
        * a ScrollArea rather than a plain overflow Box. It scrolls because the
        * flex column above caps at 60dvh and this item shrinks to fit. */}
      <ScrollArea>
        <MapInfoboxContentView
          {...props}
          addressDetails={addressDetails}
          issues={issues}
          loading={loading}
          onActivityClick={handleActivityClick}
          onIssueClick={handleIssueClick}
        />
      </ScrollArea>
    </Box>
  );
}
